import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Animate numeric values from 0 to target on viewport enter.
 * Format options:
 *   - "number": 1 234 567
 *   - "compact": 1,42M / 3,8M
 *   - "decimal": 6,75
 *   - "float1": 2,9
 */
export default function CountUp({
  value,
  format = "number",
  suffix = "",
  prefix = "",
  duration = 1800,
  className = "",
  testId,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [display, setDisplay] = useState(formatVal(0, format));

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const from = 0;
    const to = value;

    const step = (t) => {
      const progress = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (to - from) * eased;
      setDisplay(formatVal(current, format));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, format, duration]);

  return (
    <motion.span
      ref={ref}
      data-testid={testId}
      className={`font-mono-data ${className}`}
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.4 }}
    >
      {prefix}
      {display}
      {suffix}
    </motion.span>
  );
}

function formatVal(v, format) {
  if (format === "compact") {
    if (v >= 1_000_000) {
      const mm = v / 1_000_000;
      return mm.toFixed(mm < 10 ? 2 : 1).replace(".", ",") + " M";
    }
    if (v >= 1000) {
      const k = v / 1000;
      return k.toFixed(k < 10 ? 1 : 0).replace(".", ",") + " k";
    }
    return Math.round(v).toString();
  }
  if (format === "decimal") {
    return v.toFixed(2).replace(".", ",");
  }
  if (format === "float1") {
    return v.toFixed(1).replace(".", ",");
  }
  // default "number"
  return Math.round(v).toLocaleString("fr-FR").replace(/,/g, " ");
}
